import { useEffect, useState } from "react";
import { Check, Loader2, Printer, RefreshCw, X } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { fmtD } from "../lib/units";
import { printSubInvoice } from "../plans/SubscriptionPanel";

const CYC = { monthly: "Monthly", half_yearly: "Half-yearly", yearly: "Annual" };

function PlanCheck() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.get("/admin/razorpay/plans").then((r) => setD(r.data)).catch((e) => setErr(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const create = async () => { setBusy(true); setErr(""); try { await api.post("/admin/razorpay/test-plans"); await load(); } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); } };
  const hook = `${process.env.REACT_APP_BACKEND_URL}${d?.webhook_path || "/api/razorpay/webhook"}`;
  return (
    <>
      <div className="admin-head"><div><span className="overline">RAZORPAY · {(d?.mode || "").toUpperCase()} MODE</span><h1>Plan verification</h1>
        <p>Each mapped plan is fetched from Razorpay and checked for amount, currency and billing period before checkout is allowed.</p></div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="admin-btn" onClick={load} data-testid="rzp-verify-refresh"><RefreshCw size={13} /> Re-check</button>
          {d?.mode === "test" && d.rows.some((r) => !r.plan_id) && <button className="admin-btn" disabled={busy} onClick={create} data-testid="rzp-create-test-plans">{busy ? <Loader2 className="spin" size={13} /> : null} Create test plans</button>}
        </div></div>
      {err && <div className="auth-alert error">{err}</div>}
      {d && <p className="form-hint" data-testid="rzp-webhook-url">Webhook URL (this environment): <b>{hook}</b> · secret {d.webhook_secret_set ? "set" : "NOT set"}</p>}
      {d && <div className="table-wrap"><table data-testid="rzp-plan-table"><thead><tr><th>Edition</th><th>Tier</th><th>Cycle</th><th>Price (incl. GST)</th><th>{d.mode} plan ID</th><th>Live plan ID</th><th>Check</th></tr></thead><tbody>
        {d.rows.map((r) => (
          <tr key={r.edition + r.tier + r.cycle} data-testid={`rzp-plan-${r.edition}-${r.tier}-${r.cycle}`}>
            <td>{r.edition === "hotel" ? "Restaurant + Hotel" : "Restaurant only"}</td><td>{r.tier}</td><td>{CYC[r.cycle]}</td><td>{money(r.price)}</td><td>{r.plan_id || "—"}</td><td>{r.live_plan_id}</td>
            <td>{r.ok ? <span className="green-text"><Check size={13} /> OK</span> : <span className="orange-text"><X size={13} /> {(r.problems || []).join("; ") || r.error}</span>}</td>
          </tr>
        ))}
      </tbody></table></div>}
    </>
  );
}

function Profile() {
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/billing-profile").then((r) => setF(r.data)).catch(() => {}); }, []);
  const save = async (e) => { e.preventDefault(); setMsg(""); try { setF((await api.put("/admin/billing-profile", f)).data); setMsg("Saved"); } catch (x) { setMsg(errorMessage(x)); } };
  if (!f) return null;
  const I = (k, label, ph) => <label>{label}<input value={f[k] || ""} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`billing-${k}`} /></label>;
  return (
    <form onSubmit={save} className="admin-form" data-testid="billing-profile-form">
      <h3>GST invoice details (seller)</h3>
      {I("legal_name", "Legal name")}{I("gstin", "GSTIN", "Leave blank until registered — receipts are issued meanwhile")}{I("state", "Registered state", "e.g. Maharashtra")}
      {I("address", "Registered address")}{I("sac", "SAC code")}{I("email", "Billing email")}
      {msg && <div className="auth-alert info" data-testid="billing-profile-msg">{msg}</div>}
      <button className="primary-button" data-testid="billing-profile-save"><Check size={14} /> Save</button>
    </form>
  );
}

function Invoices() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/subscription-invoices").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const open = async (id) => printSubInvoice((await api.get(`/subscribe/invoices/${id}`)).data);
  return (
    <div className="table-wrap" style={{ marginTop: 20 }}><table data-testid="admin-sub-invoices"><thead><tr><th>Invoice</th><th>Date</th><th>Customer</th><th>Plan</th><th>Taxable</th><th>GST</th><th>Total</th><th></th></tr></thead><tbody>
      {rows.map((i) => <tr key={i.id}><td>{i.invoice_no}</td><td>{fmtD(i.created_at)}</td><td>{i.buyer?.name}</td><td>{i.description}</td><td>{money(i.taxable)}</td><td>{money(i.gst_total)}</td><td><b>{money(i.amount)}</b></td>
        <td><button className="icon-button" onClick={() => open(i.id)} data-testid={`admin-sub-invoice-print-${i.id}`}><Printer size={13} /></button></td></tr>)}
      {!rows.length && <tr><td colSpan={8}>No subscription payments yet.</td></tr>}
    </tbody></table></div>
  );
}

export default function RazorpayAdmin() {
  return <div data-testid="razorpay-admin"><PlanCheck /><Profile /><Invoices /></div>;
}
